import { Box, Stack } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { TriggeredHook } from "sonobello.utilities.react";

import { LeadResult } from "../../../../dtos/LeadResult";
import { Step } from "../../../../types/Step";
import { isMobileView } from "../../../../utils/Constants";
import { IFlowStepProps } from "../../../Types/IFlow";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import MedicalQuestionSequence from "../Compositions/MedicalQuestionSequence";
import IMedicalQuestionnaireCompleteEvaluator from "../Types/IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "../Types/IMedicalQualificationNonCandidateEvaluator";
import { IMedicalStepProps } from "./FormSeriesMedicalStep";

export interface IAutoScrollMedicalStepConfig {
  /** Evaluates the full form for completeness and non-candidacy. */
  medicalQualificationEvaluator: IMedicalQualificationNonCandidateEvaluator & IMedicalQuestionnaireCompleteEvaluator;
  /** The step to navigate to after all questions are answered and the user is not a non-candidate. */
  nextStep: Step;
  /** A hook to be called when the user is disqualified as a non-candidate. */
  useCreateNonCandidateLeadResult: TriggeredHook<LeadResult, undefined, boolean>;
}

/** Renders all medical questions on a single scrollable page.
 * When any answer changes, the page scrolls to the first incomplete question — but only if
 * that question is currently outside the viewport. The initial page load does not trigger a scroll. */
const AutoScrollMedicalStep: React.FC<IMedicalStepProps & IAutoScrollMedicalStepConfig & IStepWrapperButtonProps> = ({
  medicalQualificationEvaluator,
  nextStep,
  router,
  value,
  onMedicalNoncandidateDisqualification,
  setMedicalConclusions,
  setMedicalFormState,
  useCreateNonCandidateLeadResult,
  NextButtonMobile,
  NextButtonDesktop
}) => {
  const isMobile = isMobileView();
  const flowStepProps = useOutletContext<IFlowStepProps>();
  const { execute: createNonCandidateLeadResult, result, isLoading } = useCreateNonCandidateLeadResult();

  const questionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const questionsContainerRef = useRef<HTMLDivElement | null>(null);
  const isInitialMount = useRef(true);
  const prevValueRef = useRef(value);

  const handleNext = () => {
    if (medicalQualificationEvaluator.isNonCandidate(value)) {
      onMedicalNoncandidateDisqualification();
      createNonCandidateLeadResult();
    } else {
      flowStepProps.setStep(nextStep);
    }
  };

  useEffect(() => {
    if (result) router.goToNonCandidateView();
  }, [result]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevValueRef.current = value;
      return;
    }

    const prevValue = prevValueRef.current;
    prevValueRef.current = value;

    // Wait for MUI Collapse animations (~300 ms) to finish pushing content down before
    // measuring bounding rects.
    const timer = setTimeout(() => {
      const BUTTON_BANNER_HEIGHT = 72;
      const effectiveViewportBottom = window.innerHeight - BUTTON_BANNER_HEIGHT;

      const scrollToContainerBottom = () => {
        if (!questionsContainerRef.current) return;
        const containerBottom = questionsContainerRef.current.getBoundingClientRect().bottom;
        if (containerBottom > effectiveViewportBottom)
          window.scrollTo({ top: window.scrollY + containerBottom - effectiveViewportBottom, behavior: "smooth" });
      };

      // If any answer just changed to "Yes", the question may reveal non-interactive
      // informational content via Collapse — scroll unconditionally to show it.
      const anyNewYesAnswer = Object.keys(value).some(
        key => value[key] === true && prevValue[key] !== true
      );
      if (anyNewYesAnswer) {
        scrollToContainerBottom();
        return;
      }

      // Standard rule: only scroll if all remaining unanswered questions are off-screen.
      const incompleteIndices = MedicalQuestionSequence.map((q, i) => i).filter(
        i => !MedicalQuestionSequence[i].isComplete(value)
      );
      if (incompleteIndices.length === 0) return;

      const anyIncompleteVisible = incompleteIndices.some(i => {
        const el = questionRefs.current[i];
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < effectiveViewportBottom;
      });

      if (!anyIncompleteVisible) scrollToContainerBottom();
    }, 350);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <StepWrapper
      NextButtonMobile={NextButtonMobile}
      NextButtonDesktop={NextButtonDesktop}
      nextButtonLoading={isLoading}
      onNext={handleNext}
      disabled={isLoading || !medicalQualificationEvaluator.isComplete(value)}
    >
      <Box display="flex" justifyContent="center" width="100%">
        <Stack ref={questionsContainerRef} spacing={1.5} sx={{ width: "100%", maxWidth: 600, pb: "40px" }}>
          {MedicalQuestionSequence.map(({ Component }, index) => (
            <div key={index} ref={el => void (questionRefs.current[index] = el)}>
              <Component
                value={value}
                onChange={s => setMedicalFormState(o => ({ ...o, ...s }))}
                isMobile={isMobile}
                setMedicalConclusions={setMedicalConclusions}
                setMedicalFormState={setMedicalFormState}
              />
            </div>
          ))}
        </Stack>
      </Box>
    </StepWrapper>
  );
};

export default AutoScrollMedicalStep;
